import { t, countryName } from '../i18n/index.js';
import { flagEmoji } from '../domain/flagEmoji.js';
import { topOpportunities } from '../domain/marketOpportunity.js';

/**
 * Render the Market Opportunity panel into `container` (issue #118).
 *
 * Ranks the loaded storefronts by a blended, client-side opportunity score and
 * surfaces the top markets to launch / localize next, each tagged with the
 * factor that drove it. Hidden when no row is scorable.
 *
 * @param {Array<import('../domain/normalizeApp.js').RegionRow>} rows
 * @param {HTMLElement|null} container
 * @param {{ nowMs?: number, n?: number }} [options]
 */
export function renderMarketOpportunity(rows, container, options = {}) {
  if (!container) return;

  const { nowMs, n = 5 } = options;
  const top = topOpportunities(rows, { nowMs, n });

  if (top.length === 0) {
    container.hidden = true;
    container.innerHTML = '';
    return;
  }

  const items = top
    .map((entry) => {
      const name = countryName(entry.countryCode, entry.countryName);
      const factorLabel = entry.topFactor ? t(`marketOpp.factor.${entry.topFactor}`) : '';
      const tier = scoreTier(entry.score);
      const ariaLabel = t('marketOpp.itemAriaLabel', {
        rank: entry.rank,
        country: name,
        score: entry.score,
        factor: factorLabel || '—',
      });
      const factorTag = factorLabel
        ? `<span class="market-opportunity__factor">${factorLabel}</span>`
        : '';
      return `
        <li class="market-opportunity__item" aria-label="${escapeAttr(ariaLabel)}">
          <span class="market-opportunity__rank" aria-hidden="true">${entry.rank}</span>
          <span class="market-opportunity__flag" aria-hidden="true">${flagEmoji(entry.countryCode)}</span>
          <span class="market-opportunity__country">${escapeHtml(name)}</span>
          ${factorTag}
          <span class="market-opportunity__score market-opportunity__score--${tier}" aria-hidden="true">${entry.score}</span>
        </li>`;
    })
    .join('');

  container.innerHTML = `
    <details class="market-opportunity-details" open>
      <summary class="market-opportunity-summary">${t('marketOpp.title')}</summary>
      <div class="market-opportunity-body">
        <p class="market-opportunity__subtitle">${t('marketOpp.subtitle')}</p>
        <ol class="market-opportunity__list" aria-label="${escapeAttr(t('marketOpp.title'))}">${items}</ol>
        <p class="market-opportunity__note">${t('marketOpp.note')}</p>
      </div>
    </details>
  `.trim();

  container.hidden = false;
}

/**
 * Coarse tier for score-badge colouring.
 * @param {number} score
 * @returns {'high'|'med'|'low'}
 */
function scoreTier(score) {
  if (score >= 70) return 'high';
  if (score >= 40) return 'med';
  return 'low';
}

function escapeHtml(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function escapeAttr(value) {
  return escapeHtml(value).replace(/"/g, '&quot;');
}
